import PropTypes from 'prop-types'
import {useEffect, useRef} from 'react'
import parseISO from 'date-fns/parseISO'
import parse from 'date-fns/parse'
import isValid from 'date-fns/isValid'
import sortBy from 'lodash/sortBy.js'
import classNames from 'classnames'

import {formatCurrency, isTBD} from '../../common/components/Currency.js'
import {
  RateShape,
  BulkRateShape,
  RateErrorShape,
} from '../../common/PropTypes.js'
import {
  formsSelector,
  getState,
  updateForm,
  useAutoForm,
  useSelector,
} from '../../store.js'
import Zropdown, {
  useZropdown,
  useZropdownElement,
  POP_UP,
} from '../../common/components/Zropdown.js'
import {currentDropdownSelector} from '../../redux/selectors/ui/index.js'
import cn from '../../common/className.js'
import {
  rateKeySelector,
  shipperIDsSelector,
  sortByDeliveryEstimate,
  updateLabelInfo,
  ratesSelector,
  labelConfigSelector,
} from '../../data/labelInfos/index.js'
import Select from '../../common/components/Select.js'
import Radio from '../../common/components/Radio.js'
import {useLabelConfigContext} from './LabelConfigContext.js'
import {
  AMAZON_SFP,
  HAS_CARRIER_LOGO,
  MULTI_CARRIER_SHIPPER,
  USPS_SHIPPERS,
} from '../../common/constants/ShipperNames.js'
import ShipperOptions from '../../data/shipperOptions.js'
import {dateTimeFormat, formatMonthDay} from '../../common/date.js'
import {getCarrierFromRate} from '../../data/labelInfos/rateRequest.js'
import {
  getMultiCarrierName,
  getShipperName,
  shippersSelector,
} from '../../data/shippers.js'
import {formatPreferredShippingMethodsProperty} from './Fields/ShippingMethodFilter.js'
import Icon from '../../common/icons/Icon.js'

const RateSortOptions = [
  {
    value: 'cost_0-9',
    display: 'Cost - Lowest to Highest',
    sort: ['cost', 'service_type'],
  },
  {
    value: 'cost_9-0',
    display: 'Cost - Highest to Lowest',
    sort: ['cost', 'service_type'],
    reverse: true,
  },
  {
    value: 'delivery_0-9',
    display: 'Estimated Delivery - Fastest to Slowest',
    sort: [sortByDeliveryEstimate, 'cost', 'service_type'],
  },
  {
    value: 'delivery_9-0',
    display: 'Estimated Delivery - Slowest to Fastest',
    sort: [sortByDeliveryEstimate, 'cost', 'service_type'],
    reverse: true,
  },
  {
    value: 'carrier_a-z',
    display: 'Carrier - A-Z',
    sort: ['shipper_name', 'cost', 'service_type'],
  },
  {
    value: 'carrier_z-a',
    display: 'Carrier - Z-A',
    sort: ['shipper_name', 'cost', 'service_type'],
    reverse: true,
  },
  {
    value: 'preferred_true-false',
    display: 'Preferred - ↓',
    sort: [({is_preferred}) => !is_preferred, 'cost', 'service_type'],
  },
  {
    value: 'preferred_false-true',
    display: 'Preferred - ↑',
    sort: [({is_preferred}) => !is_preferred, 'cost', 'service_type'],
    reverse: true,
  },
]

export function formatRate(rate, currencySymbol) {
  let formattedETA = ''
  let formattedCost = ''

  if (rate.estimated_delivery_date) {
    let date = parseISO(rate.estimated_delivery_date)

    // for dumb dates from Endicia: month/day/year
    if (!isValid(date)) {
      date = parse(rate.estimated_delivery_date, 'M/d/yyyy', new Date())
    }

    if (isValid(date)) {
      formattedETA = dateTimeFormat(
        date,
        formatMonthDay,
        rate.carrier === AMAZON_SFP
          ? {
              timeZone: 'America/Los_Angeles',
            }
          : {},
      )
    }
  }

  if (rate.estimated_delivery_days) {
    const days = rate.estimated_delivery_days
    formattedETA = `${days} ${days === '1' ? 'day' : 'days'}`
  }

  if (rate.disabled) {
    formattedCost = 'Not available for all orders'
  } else if (isTBD(rate.cost)) {
    formattedCost = 'TBD'
  } else {
    formattedCost = formatCurrency(rate.cost, currencySymbol)
  }

  return {formattedETA, formattedCost}
}

function formatShipperName(rate, shippers, shipperCount) {
  const shipper = shippers[rate.shipper_id]

  return [
    shipperCount > 1 ? getShipperName(shipper) : '',
    MULTI_CARRIER_SHIPPER.includes(rate.vendor)
      ? getMultiCarrierName(shipper, rate.carrier_account_id)
      : '',
  ]
    .filter((v) => v)
    .join(' - ')
}

function carrierCountByTypeSelector(state, {labelInfoID}) {
  const rates = ratesSelector(state, {labelInfoID})

  const carrierCountByType = {}
  for (const rate of rates) {
    const carrier = USPS_SHIPPERS.includes(rate.vendor)
      ? 'usps'
      : rate.carrier || rate.vendor

    carrierCountByType[carrier] ||= {}

    const id = MULTI_CARRIER_SHIPPER.includes(rate.vendor)
      ? rate.carrier_account_id
      : rate.shipper_id

    carrierCountByType[carrier][id] = (carrierCountByType[carrier][id] || 0) + 1
  }

  return Object.entries(carrierCountByType).reduce((prev, [key, value]) => {
    prev[key] = Object.keys(value).length

    return prev
  }, {})
}

function RateRow({
  rate,
  isSelected,
  isRateSelected,
  applyRate,
  hasMultipleShippersOfSameCarrier,
  carrier,
  additionalColumns,
}) {
  const ref = useRef()
  const columnCount = 6 + additionalColumns.length
  carrier ||= rate.vendor

  useZropdownElement({ref, isSelected})

  return (
    <>
      {rate.error_message ? (
        <tr ref={ref} className="tr--has-error" data-dropdown-prevent-close>
          <td className="table__td align-center" colSpan={columnCount}>
            <div className="error">
              <span>{rate.shipper_name} - </span>
              {rate.error_message}
            </div>
          </td>
        </tr>
      ) : (
        <tr
          ref={ref}
          className={cn`tr--rs
          ${{'tr--focus': isSelected, 'tr--rate-selected': isRateSelected}}`}
          onClick={() => applyRate(rate)}
        >
          <td className="table__td">
            <Radio
              mode="fancy"
              checked={isRateSelected}
              onChange={() => applyRate(rate)}
            />
          </td>
          <td className="table__td table__td--carrier-logo">
            {HAS_CARRIER_LOGO.includes(carrier) ? (
              <>
                <div className={cn`td__shipper-logo ${carrier}`} />
                {hasMultipleShippersOfSameCarrier && (
                  <div className="fs-n2 lh-md align-center margin-top-5 op-50">
                    {rate.shipper_name}
                  </div>
                )}
              </>
            ) : (
              <div>{rate.shipper_name}</div>
            )}
          </td>
          <td className="table__td">
            <span className="lh-md v-align-middle margin-right-5">
              {rate.service}
            </span>
            {rate.is_preferred && (
              <span
                className="tooltip--top lh-md"
                data-hint="Preferred shipping method"
              >
                <Icon name="star" className="fs-01 text--warning" />
              </span>
            )}
          </td>
          <td className="table__td">
            <span className="lh-md">{rate.box_shape_name}</span>
            {rate.one_rate && (
              <div className="margin-top-3">
                <strong className="label__callout label__callout--fedex v-align-middle padding-top-3 padding-bottom-3 lh-1-1">
                  One Rate
                </strong>
              </div>
            )}
          </td>
          {additionalColumns.map(({value, cellClassName}, index) => (
            <td key={index} className={cn`table__td ${cellClassName}`}>
              {value(rate) != null && value(rate) !== '' ? (
                <span className="lh-md">{value(rate)}</span>
              ) : (
                <span className="lh-md op-50">—</span>
              )}
            </td>
          ))}
          <td className="table__td">
            <div className="lh-md">{rate.formattedETA}</div>
            {rate.saturday_delivery && (
              <div className="margin-top-3">
                <strong className="label__callout label__callout--yellow v-align-middle padding-top-3 padding-bottom-3 lh-1-1">
                  Saturday Delivery
                </strong>
              </div>
            )}
          </td>
          <td className="table__td align-right">
            <strong className="lh-md">{rate.formattedCost}</strong>
          </td>
        </tr>
      )}
    </>
  )
}

function getAdditionalColumns(rates) {
  const [additionalColumns] = rates.reduce(
    (prev, rate) => {
      if (rate.processing_category) {
        if (!prev[1].lastProcessingCategory) {
          prev[1].lastProcessingCategory = rate.processing_category
        } else if (
          prev[1].lastProcessingCategory !== rate.processing_category &&
          !prev[1].hasProcessingCategory
        ) {
          prev[0].push({
            value: (rate) =>
              ShipperOptions[rate.vendor]?.processing_category?.find(
                ({value}) => value === rate.processing_category,
              )?.display ||
              rate.processing_category ||
              '',
            display: 'Processing Cat.',
            className: 'w-20',
            cellClassName: '',
          })

          prev[1].hasProcessingCategory = true
        }
      }

      return prev
    },
    [[], {}],
  )

  return additionalColumns
}

function updateRatesResults(
  dropdown,
  rates,
  value,
  currencySymbol,
  labelInfoID,
  config,
) {
  try {
    const state = getState()
    const form = formsSelector(state)[dropdown]
    const rateKey = rateKeySelector(state, {labelInfoID})
    const shippers = shippersSelector(state)
    const shipperCount = shipperIDsSelector(state, {labelInfoID}).length

    if (!form) {
      return
    }

    let results = rates.map((rate) => ({
      ...rate,
      ...formatRate(rate, currencySymbol),
      shipper_name: formatShipperName(rate, shippers, shipperCount),
      is_preferred: (
        config[formatPreferredShippingMethodsProperty(rate.vendor)] || []
      ).includes(rate.service_type),
      disabled: !!rate.error_message,
    }))

    const sortOption = RateSortOptions.find(
      (option) => option.value === form.sort,
    )

    results = sortBy(results, sortOption.sort)

    if (sortOption.reverse) {
      results.reverse()
    }

    const [selectedRateIndex, selectedRate] = results.reduce(
      (prev, rate, index) => {
        if (rate.key && rate.key === rateKey) {
          prev = [index, rate]
        }

        return prev
      },
      [-1, null],
    )

    updateForm(dropdown, {
      results,
      selectedIndex: selectedRateIndex !== -1 ? selectedRateIndex : 0,
      hasFoundNothing: results.length === 0,
      selectedRateIndex,
      selectedRate,
      additionalColumns: getAdditionalColumns(results),
    })
  } catch (err) {
    updateForm(dropdown, {
      serverError: err.message,
    })
  }
}

export default function RatePopOut({
  value,
  onChange,
  rates,
  updated,
  loading,
  disabled,
  hasErrors,
  hasWarnings,
  hasOnlyDisabledRates,
  hasNoRates,
  currencySymbol,
  dropdown,
}) {
  dropdown = dropdown || 'RATE_POP_OUT'
  const {labelInfoID} = useLabelConfigContext()

  const form = useAutoForm(dropdown, {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    serverError: null,
    sort: RateSortOptions[0].value,
    additionalColumns: [],
  })
  const config = useSelector((state) =>
    labelConfigSelector(state, {labelInfoID}),
  )

  useEffect(() => {
    updateRatesResults(
      dropdown,
      rates,
      value,
      currencySymbol,
      labelInfoID,
      config,
    )
  }, [rates, value, currencySymbol, labelInfoID, form.sort, config])

  const buttonRef = useRef()

  const {onSelectInternal, onConfirm, onUp, onDown} = useZropdown({
    dropdown,
    form,
    onSelect: (option) => onChange(option),
    closeOnConfirm: true,
  })

  const currentDropdown = useSelector(currentDropdownSelector)
  const isOpen = currentDropdown === dropdown

  const shipperIDs = useSelector((state) =>
    shipperIDsSelector(state, {labelInfoID}),
  )
  const carrierCountByType = useSelector((state) =>
    carrierCountByTypeSelector(state, {labelInfoID}),
  )
  let needsShipperHeader = false
  let lastShipperID = null
  const sortedByCarrier = !!form.sort.match(/carrier/)
  const {selectedRate, additionalColumns} = form
  const hasAdditionalColumns = additionalColumns.length > 0

  useEffect(() => {
    if (!isOpen || !updated) {
      return
    }

    updateLabelInfo(labelInfoID, {rates_updated: false})
  }, [isOpen, updated])

  return (
    <div
      className={`wrap-outer--tag-dropdown-with-filter wrap--shipping-rate-dropdown-rs ${
        isOpen ? 'open' : ''
      }`}
      data-dropdown={dropdown}
    >
      <button
        ref={buttonRef}
        className={cn`btn btn--dropdown btn--filter btn--filter-w-border w-100 ${{
          'select--updated': updated,
          'select--loading': loading,
          'select--error': hasErrors || hasOnlyDisabledRates,
          'select--warning':
            hasWarnings && !(hasErrors || hasOnlyDisabledRates),
        }}`}
        type="button"
        disabled={loading || disabled || rates.length === 0}
      >
        <span className="btn__text flex--justify">
          {selectedRate ? (
            <>
              <span className="wrap--rate-shipper-pkg-info txt-overflow-ellipsis">
                <span
                  className={cn`wrap--selected-rate-service block txt-overflow-ellipsis lh-md ${getCarrierFromRate(selectedRate)}`}
                >
                  <span className="fs-n0 lh-md v-align-middle">
                    {selectedRate.service}
                  </span>
                </span>
                <span className="block lh-md txt-overflow-ellipsis">
                  {selectedRate.box_shape_name}
                </span>
              </span>
              <span className="wrap--rate-cost-eta-info">
                <strong className="block lh-md">
                  {selectedRate.formattedCost}
                </strong>
                {selectedRate.formattedETA && (
                  <span className="op-75 lh-md">
                    (ETA {selectedRate.formattedETA})
                  </span>
                )}
              </span>
            </>
          ) : hasNoRates ? (
            <em>No shipping rates are currently available.</em>
          ) : (
            'View shipping rates...'
          )}
        </span>
      </button>
      <Zropdown
        className="wrap--base-dropdown-rs"
        dropdown={dropdown}
        parentRef={buttonRef}
        onConfirm={onConfirm}
        onUp={onUp}
        onDown={onDown}
        header={
          <div
            className="zropdown__header zropdown__header--rs"
            data-dropdown-prevent-close
          >
            <div className="zropdown__header--rs-inner flex--justify">
              <p className="fs-02 lh-md margin-bottom-0">
                <strong>Shipping Methods/Rates</strong>
              </p>
              <Select
                value={form.sort}
                onChange={(sort) => updateForm(dropdown, {sort})}
                options={RateSortOptions}
              ></Select>
            </div>
          </div>
        }
        blurOnOpen
        minimumHeight={500}
        positionVert={POP_UP}
      >
        <li className="list__item list--style-none wrap--table--rs">
          <table className="table table--rs">
            <thead>
              <tr>
                <th className="table__th">&nbsp;</th>
                <th className="table__th table__th--carrier-logo w-15 align-center">
                  Carrier
                </th>

                <th
                  className={classNames(
                    'table__th',
                    hasAdditionalColumns ? 'w-20' : 'w-35',
                  )}
                >
                  Shipping Method
                </th>
                <th
                  className={classNames(
                    'table__th',
                    hasAdditionalColumns ? 'w-20' : 'w-25',
                  )}
                >
                  Package Type
                </th>
                {additionalColumns.map(({display, className}, index) => (
                  <th className={cn`table__th ${className}`} key={index}>
                    {display}
                  </th>
                ))}
                <th
                  className={classNames(
                    'table__th',
                    hasAdditionalColumns ? 'w-15' : 'w-10',
                  )}
                >
                  ETA
                </th>
                <th
                  className={classNames(
                    'table__th align-right',
                    hasAdditionalColumns ? 'w-10' : 'w-15',
                  )}
                >
                  Rate
                </th>
              </tr>
            </thead>
            <tbody className="table__tbody--lines table__tbody--rs table__tbody--v-align-middle">
              {form.results.map((rate, index) => {
                needsShipperHeader =
                  sortedByCarrier &&
                  shipperIDs.length > 1 &&
                  rate.shipper_id !== lastShipperID
                lastShipperID = rate.shipper_id
                const carrier = getCarrierFromRate(rate)

                return (
                  <RateRow
                    key={`${rate.key}_${index}`}
                    rate={rate}
                    isSelected={index === form.selectedIndex}
                    isRateSelected={index === form.selectedRateIndex}
                    applyRate={() => onSelectInternal(rate)}
                    needsShipperHeader={needsShipperHeader}
                    needsShipperInRow={!sortedByCarrier}
                    hasMultipleShippersOfSameCarrier={
                      carrierCountByType[carrier] > 1
                    }
                    carrier={carrier}
                    additionalColumns={additionalColumns}
                  />
                )
              })}
            </tbody>
          </table>
        </li>
      </Zropdown>
    </div>
  )
}

RatePopOut.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  rates: PropTypes.arrayOf(
    PropTypes.oneOfType([RateShape, RateErrorShape, BulkRateShape]),
  ),
  updated: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  hasErrors: PropTypes.bool,
  hasWarnings: PropTypes.bool,
  hasOnlyDisabledRates: PropTypes.bool,
  hasNoRates: PropTypes.bool,
  currencySymbol: PropTypes.string.isRequired,
  dropdown: PropTypes.string,
}
